import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock, Shield } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../config/stripe';

// Usar chave do arquivo de configuração
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const PaymentForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { items, total, shipping, zipCode, finalTotal, clearCart } = useCart();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: zipCode || ''
  });

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      setError(t('checkout.stripeNotLoaded'));
      return;
    }

    // Validar campos
    if (!customerInfo.name || !customerInfo.email) {
      setError(t('checkout.fillRequiredFields'));
      return;
    }

    // Validar código postal
    if (!zipCode || zipCode.length < 4) {
      setError(t('checkout.validPostalCode'));
      return;
    }

    setIsProcessing(true);
    setError(null);
    
    try {
      // 1. Criar Payment Intent no backend
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL || 'https://snatchedlatinas.ch/api'}/payment/create-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: finalTotal, // Valor em CHF — backend multiplica por 100
          currency: 'chf',
          customer: customerInfo,
          shipping_cost: shipping,
          shipping_zipcode: zipCode,
          items: items.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            name: item.product?.name,
            price: item.product?.price
          }))
        })
      });

      if (!response.ok) {
        throw new Error(t('checkout.paymentCreateError'));
      }

      const { clientSecret } = await response.json();
      
      if (!clientSecret) {
        throw new Error(t('checkout.clientSecretMissing'));
      }

      // 2. Confirmar pagamento com Stripe Elements
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error(t('checkout.cardElementNotFound'));
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: customerInfo.name,
            email: customerInfo.email,
            phone: customerInfo.phone || undefined
          }
        }
      });

      if (stripeError) {
        setError(stripeError.message || t('checkout.paymentProcessingError'));
      } else if (paymentIntent?.status === 'succeeded') {
        // Save order to Supabase via backend
        try {
          await fetch(`${import.meta.env.VITE_BACKEND_URL || 'https://snatchedlatinas.ch/api'}/payment/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              paymentIntentId: paymentIntent.id,
              customerEmail: customerInfo.email,
              orderDetails: {
                name: customerInfo.name,
                phone: customerInfo.phone,
                address: customerInfo.address,
                city: customerInfo.city,
                state: customerInfo.state,
                zipCode: customerInfo.zipCode,
                amount: finalTotal,
                shipping,
                items: items.map(item => ({
                  product_id: item.product_id,
                  quantity: item.quantity,
                  name: item.product?.name,
                  price: item.product?.price,
                  image: item.product?.image_url
                }))
              }
            })
          });
        } catch (confirmError) {
          console.error('⚠️ Erro ao salvar pedido (pagamento já confirmado):', confirmError);
        }

        // Limpar carrinho
        await clearCart();
        
        // Redirecionar para página de sucesso
        window.location.href = `/success?payment_intent=${paymentIntent.id}`;
      } else {
        setError(t('checkout.paymentNotCompleted') + ': ' + paymentIntent?.status);
      }
    } catch (err: any) {
      setError(err.message || t('checkout.paymentProcessingError'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePayment} className="space-y-6">
      {/* Informações do Cliente */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg flex items-center text-gray-900">
          <CreditCard className="w-5 h-5 mr-2 text-[#4ECDC4]" />
          {t('checkout.paymentInfo')}
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('checkout.fullName')} *
          </label>
          <input
            type="text"
            required
            value={customerInfo.name}
            onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
            placeholder="María González"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email *
          </label>
          <input
            type="email"
            required
            value={customerInfo.email}
            onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
            placeholder="maria@example.com"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('checkout.phone')} ({t('checkout.optional')})
          </label>
          <input
            type="tel"
            value={customerInfo.phone}
            onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
            placeholder="+41 79 123 45 67"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
          />
        </div>
      </div>

      {/* Cartão de Crédito */}
      <div className="border-t pt-6">
        <h3 className="font-semibold text-lg mb-4 text-gray-900">{t('checkout.cardDetails')}</h3>
        <div className="p-4 border border-gray-300 rounded-lg bg-white">
          <CardElement options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                '::placeholder': {
                  color: '#aab7c4'
                }
              },
              invalid: {
                color: '#9e2146'
              }
            }
          }} />
        </div>
        
        {/* Mensagem de Erro */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Informações de Segurança */}
        <div className="mt-4 flex items-center text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
          <Shield className="w-5 h-5 mr-2 text-green-600" />
          <span>{t('checkout.securePaymentStripe')}</span>
        </div>
        
        {/* Botão de Pagamento */}
        <button
          type="submit"
          disabled={!stripe || isProcessing || !customerInfo.name || !customerInfo.email || !zipCode}
          className="w-full mt-6 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
              {t('checkout.processing')}
            </>
          ) : (
            <>
              <Lock className="w-5 h-5 mr-2" />
              {t('checkout.pay')} CHF {finalTotal.toFixed(2)}
            </>
          )}
        </button>

        {/* Cartões de Teste */}
        {import.meta.env.MODE === 'development' && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm font-semibold text-yellow-800 mb-2">🧪 Modo de Teste - Use:</p>
            <p className="text-xs text-yellow-700">Cartão: 4242 4242 4242 4242</p>
            <p className="text-xs text-yellow-700">Data: Qualquer futura | CVV: Qualquer 3 dígitos</p>
          </div>
        )}
      </div>
    </form>
  );
};

const CheckoutNew = () => {
  const { items, total, shipping, zipCode, finalTotal } = useCart();
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('cart.empty')}</h2>
          <p className="text-gray-600 mb-6">{t('cart.addProductsFirst')}</p>
          <Link 
            to="/produtos"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white rounded-lg font-bold hover:shadow-lg transition-all"
          >
            {t('cart.viewProducts')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <Link to="/carrinho" className="inline-flex items-center text-[#4ECDC4] hover:text-[#95C11F] transition-colors mb-8 font-medium">
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t('checkout.backToCart')}
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('checkout.title')}</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Formulário de Pagamento */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <Elements stripe={stripePromise}>
                <PaymentForm />
              </Elements>
            </div>
          </div>
          
          {/* Resumo do Pedido */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 sticky top-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">{t('checkout.orderSummary')}</h2>
              
              {/* Items */}
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3">
                    <img 
                      src={item.product?.image_url} 
                      alt={item.product?.name}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-sm">{item.product?.name}</h4>
                      <p className="text-gray-600 text-xs">{t('checkout.quantity')}: {item.quantity}</p>
                    </div>
                    <p className="font-bold text-gray-900">
                      CHF {((item.product?.price || 0) * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              
              {/* Totais */}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal:</span>
                  <span>CHF {(finalTotal - shipping).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Shipping:</span>
                  {!zipCode ? (
                    <span className="text-orange-600 text-sm">Enter postal code in cart</span>
                  ) : shipping === 0 ? (
                    <span className="text-green-600 font-bold">FREE! 🎉</span>
                  ) : (
                    <span>CHF {shipping.toFixed(2)}</span>
                  )}
                </div>
                <div className="flex justify-between text-xl font-bold text-gray-900 pt-3 border-t">
                  <span>Total:</span>
                  <span className="text-[#4ECDC4]">CHF {finalTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Features */}
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <Shield className="w-4 h-4 mr-2 text-green-600" />
                  <span>{t('checkout.securePurchase')}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Lock className="w-4 h-4 mr-2 text-green-600" />
                  <span>{t('checkout.encryptedData')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutNew;
